/**
 * Message formatter for user-friendly trading messages
 * Generates contextual messages based on action type and parameters
 */

import { debugLog } from './logger';

export interface OrderMessageContext {
  symbol?: string;
  side?: 'buy' | 'sell';
  orderType?: 'market' | 'limit';
  quantity?: string;
  price?: string;
  reduceOnly?: boolean;
  isBatch?: boolean;
  batchSize?: number;
  grouping?: 'na' | 'normalTpsl' | 'positionTpsl';
  hasTPSL?: boolean;
}

export interface CancelMessageContext {
  symbol?: string;
  orderId?: number;
  isBatch?: boolean;
  cancelCount?: number;
}

export interface LeverageMessageContext {
  symbol?: string;
  leverage?: number;
  isCross?: boolean;
}

/**
 * Format a filled order message with full context
 */
export function formatFilledOrderMessage(
  filledSize: string,
  avgPrice: string,
  context?: OrderMessageContext
): string {
  if (!context?.symbol) {
    // Fallback to original format if no context
    return `Filled ${filledSize} @ ${avgPrice}`;
  }

  const { symbol, side, reduceOnly, hasTPSL, grouping } = context;

  // Check if this is a TP/SL order on existing position
  // These have grouping='positionTpsl' and size='0'
  if (grouping === 'positionTpsl') {
    // This is setting TP/SL on existing position, not closing
    return formatTpSlMessage(symbol, true, true);
  }

  // Determine action and direction
  let action: string;
  let direction: string;

  if (reduceOnly) {
    // Closing a position
    action = 'Closed';
    direction = side === 'sell' ? 'long' : 'short';
  } else {
    // Opening a position
    action = 'Opened';
    direction = side === 'buy' ? 'long' : 'short';
  }

  // Format price with dollar sign and proper decimals
  const formattedPrice = formatPrice(avgPrice);

  // Base message
  let message = `${action} ${direction}: ${filledSize} ${symbol} at ${formattedPrice}`;

  // Add TP/SL indicator if present
  if (hasTPSL && !reduceOnly) {
    message += ' (with TP/SL)';
  }

  return message;
}

/**
 * Format a resting (limit) order message
 */
export function formatRestingOrderMessage(context?: OrderMessageContext): string {
  debugLog('[SDK] formatRestingOrderMessage called with context:', JSON.stringify(context, null, 2));

  if (!context?.symbol) {
    return 'Order placed successfully';
  }

  const { symbol, side, quantity, price, reduceOnly, hasTPSL, grouping } = context;

  // Check if this is a TP/SL order on existing position
  // These have grouping='positionTpsl' and size='0'
  if (grouping === 'positionTpsl') {
    // This is setting TP/SL on existing position, not a regular order
    debugLog('[SDK] Detected positionTpsl grouping, calling formatTpSlMessage');
    return formatTpSlMessage(symbol, true, true);
  }

  if (reduceOnly) {
    // Closing order
    const direction = side === 'sell' ? 'long' : 'short';
    if (price) {
      const formattedPrice = formatPrice(price);
      return `Limit order placed to close ${direction}: ${quantity} ${symbol} at ${formattedPrice}`;
    }
    return `Order placed to close ${direction}: ${quantity} ${symbol}`;
  } else {
    // Opening order
    const sideText = side === 'buy' ? 'Buy' : 'Sell';
    let message: string;

    if (price) {
      const formattedPrice = formatPrice(price);
      message = `Limit order placed: ${sideText} ${quantity} ${symbol} at ${formattedPrice}`;
    } else {
      message = `Order placed: ${sideText} ${quantity} ${symbol}`;
    }

    // Add TP/SL indicator if present
    if (hasTPSL) {
      message += ' (with TP/SL)';
    }

    return message;
  }
}

/**
 * Format batch order success message
 */
export function formatBatchOrderMessage(
  successCount: number,
  totalCount: number,
  context?: OrderMessageContext
): string {
  if (successCount === totalCount) {
    if (context?.reduceOnly) {
      return `Successfully closed ${successCount} position${successCount > 1 ? 's' : ''}`;
    }
    return `All ${successCount} order${successCount > 1 ? 's' : ''} placed successfully`;
  } else {
    const failedCount = totalCount - successCount;
    return `${successCount} order${successCount > 1 ? 's' : ''} placed, ${failedCount} failed`;
  }
}

/**
 * Format order cancellation message
 */
export function formatCancelMessage(context?: CancelMessageContext): string {
  if (!context?.symbol) {
    return 'Order cancelled successfully';
  }

  const { symbol, orderId, isBatch, cancelCount } = context;

  if (isBatch && cancelCount) {
    return `Cancelled ${cancelCount} order${cancelCount > 1 ? 's' : ''} for ${symbol}`;
  }

  if (orderId) {
    return `Cancelled order #${orderId} for ${symbol}`;
  }

  return `Order cancelled for ${symbol}`;
}

/**
 * Format batch cancel message
 */
export function formatBatchCancelMessage(successCount: number): string {
  return `Cancelled ${successCount} order${successCount > 1 ? 's' : ''} successfully`;
}

/**
 * Format leverage update message
 */
export function formatLeverageMessage(context?: LeverageMessageContext): string {
  if (!context?.symbol || !context?.leverage) {
    return 'Leverage updated successfully';
  }

  const { symbol, leverage, isCross } = context;
  const mode = isCross ? 'cross' : 'isolated';

  return `Updated ${symbol} to ${leverage}x leverage (${mode})`;
}

/**
 * Format margin mode update message
 */
export function formatMarginModeMessage(symbol?: string, isCross?: boolean): string {
  if (!symbol) {
    return 'Margin mode updated successfully';
  }

  const mode = isCross ? 'cross' : 'isolated';
  return `Updated ${symbol} to ${mode} margin mode`;
}

/**
 * Format TP/SL update message
 */
export function formatTpSlMessage(
  symbol?: string,
  hasTP?: boolean,
  hasSL?: boolean
): string {
  if (!symbol) {
    return 'TP/SL updated successfully';
  }

  if (hasTP && hasSL) {
    return `Updated take-profit and stop-loss for ${symbol}`;
  } else if (hasTP) {
    return `Updated take-profit for ${symbol}`;
  } else if (hasSL) {
    return `Updated stop-loss for ${symbol}`;
  }

  return `Updated TP/SL for ${symbol}`;
}

/**
 * Helper function to format price with proper decimals and dollar sign
 */
function formatPrice(price: string): string {
  const numPrice = parseFloat(price);

  if (isNaN(numPrice)) {
    return price;
  }

  // Format with appropriate decimal places
  if (numPrice >= 1000) {
    return `$${numPrice.toFixed(2)}`;
  } else if (numPrice >= 1) {
    return `$${numPrice.toFixed(4)}`;
  } else {
    return `$${numPrice.toFixed(6)}`;
  }
}

/**
 * Helper to clean error messages from Hyperliquid
 */
export function cleanErrorMessage(errorMsg: string): string {
  // Remove "asset=0" or similar from error messages
  let cleaned = errorMsg.replace(/\.\s*asset=\d+/g, '');

  // Capitalize first letter
  cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);

  return cleaned;
}
